/**
 * BUG-018 (round 2): the rules of the Add / Edit Account forms (the firm's default chart and a client's chart), mirroring
 * the API (AccountCodeRules, AccountTypeClientRules). The API enforces both; these keep the forms from offering a choice the
 * server would refuse and give the field-level message at once.
 */

/** The longest account code the forms take. */
export const ACCOUNT_CODE_MAX_LENGTH = 10;

/** The old application put no limit on the characters of a code; letters, digits and / - . # & ( ) are kept, spaces are not. */
const ACCOUNT_CODE_PATTERN = /^[A-Za-z0-9/.\-#&()]+$/;

export const ACCOUNT_CODE_TOO_LONG_MESSAGE = `The account code can be at most ${ACCOUNT_CODE_MAX_LENGTH} characters.`;
export const ACCOUNT_CODE_CHARACTERS_MESSAGE =
  'The account code can only use letters, numbers and the characters / - . # & ( ) (no spaces), for example 1000 or 4610/1.';

/** Why a (trimmed) account code is refused, or null when it is acceptable; an empty code is the caller's "enter a code". */
export function accountCodeProblem(code: string | null | undefined): string | null {
  const value = code ?? '';
  if (value.length === 0) return null;
  if (value.length > ACCOUNT_CODE_MAX_LENGTH) return ACCOUNT_CODE_TOO_LONG_MESSAGE;
  return ACCOUNT_CODE_PATTERN.test(value) ? null : ACCOUNT_CODE_CHARACTERS_MESSAGE;
}

/** An account type with the client types (0 limited, 1 sole trader, 2 partnership, 3 LLP) that may use it; none = all. */
interface ClientTypeRestricted {
  id: number;
  clientTypes?: number[] | null;
}

/** True when an account type may be used for every one of the given client types (an unknown type list allows only open types). */
export function accountTypeOffered(type: ClientTypeRestricted, clientTypes: readonly number[]): boolean {
  if (!type.clientTypes || type.clientTypes.length === 0) return true;
  const allowed = type.clientTypes;
  return clientTypes.length > 0 && clientTypes.every((clientType) => allowed.includes(clientType));
}

/**
 * The account types a form offers for these client types. A type the account being edited already has stays on offer, so
 * an account saved before the rule (Partners Emoluments on a limited company) still shows and saves its type.
 */
export function accountTypesFor<T extends ClientTypeRestricted>(
  types: readonly T[],
  clientTypes: readonly number[],
  currentTypeId?: number | null,
): T[] {
  return types.filter((type) => type.id === currentTypeId || accountTypeOffered(type, clientTypes));
}
